// ℹ️ AboutPage - Page "À propos" du festival Rock'In Agde
// Présente l'histoire, les valeurs et la mission de l'événement
// Utilisé dans : src/App.jsx (route "/about")

import { Heart, Target, Users, Settings, Mail, Facebook, Instagram, MapPin, Calendar } from 'lucide-react'
import aboutData from '@/locales/about.json'
import HeroSection from '@/components/home/HeroSection'

/**
 * Page "À propos" du festival Rock'In Agde
 * Structure : Hero + Sections thématiques + Contact
 * Contenu chargé depuis about.json pour faciliter les modifications
 */
export default function AboutPage() {
  const { hero, sections, contact } = aboutData

  return (
    <div className="md:pt-16">
      {/* Hero Section */}
      <HeroSection
        badge={{ icon: Heart, text: hero.badge }}
        title={hero.title}
        description={hero.subtitle}
        features={hero.features.map(f => ({ ...f, icon: f.text === "Passion musicale" ? Target : f.text === "Communauté engagée" ? Users : MapPin }))}
      />

      {/* Sections */}
      <div className="bg-white dark:bg-neutral-900">
        {sections.map((section, index) => {
          const isEven = index % 2 === 0
          const Icon = index === 0 ? Heart : index === 1 ? Target : index === 2 ? Users : Settings

          return (
            <section
              key={index}
              className={`section-padding ${
                isEven ? 'bg-white dark:bg-neutral-900' : 'bg-neutral-50 dark:bg-neutral-800'
              }`}
            >
              <div className="container-custom">
                <div className="max-w-4xl mx-auto">
                  <div className="flex items-start gap-6 mb-6">
                    <div className="p-4 bg-primary-100 dark:bg-primary-950 rounded-2xl flex-shrink-0">
                      <Icon className="text-primary-600 dark:text-primary-400" size={32} />
                    </div>
                    <div>
                      <h2 className="text-3xl md:text-4xl font-display text-primary-600 dark:text-primary-400 mb-6">
                        {section.title}
                      </h2>
                    </div>
                  </div>
                  
                  <div className="space-y-4 text-lg text-neutral-700 dark:text-neutral-300 leading-relaxed pl-20">
                    {section.content.split('\n\n').map((paragraph, pIndex) => {
                      // Vérifier si c'est une liste (commence par 🎶, 🤝, 🌍)
                      if (paragraph.match(/^[🎶🤝🌍]/)) {
                        const items = paragraph.split('\n')
                        return (
                          <div key={pIndex} className="space-y-4">
                            {items.map((item, iIndex) => {
                              const [emoji, ...rest] = item.split(' ')
                              const text = rest.join(' ')
                              return (
                                <div key={iIndex} className="flex items-start gap-4">
                                  <span className="text-3xl flex-shrink-0">{emoji}</span>
                                  <p className="pt-1">{text}</p>
                                </div>
                              )
                            })}
                          </div>
                        )
                      }
                      return <p key={pIndex}>{paragraph}</p>
                    })}
                  </div>
                </div>
              </div>
            </section>
          )
        })}
      </div>

      {/* Contact Section */}
      <section className="section-padding bg-gradient-to-br from-primary-500 to-primary-600 text-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-12">
              {contact.title}
            </h2>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Lieu & Date */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
                <h3 className="text-xl font-bold mb-6">{contact.appointment.title}</h3>
                <div className="space-y-4 text-left">
                  <div className="flex items-start gap-3">
                    <Calendar className="text-primary-200 mt-1" size={20} />
                    <div>
                      <div className="font-medium">{contact.appointment.date.value}</div>
                      <div className="text-sm text-neutral-200">{contact.appointment.date.label}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="text-primary-200 mt-1" size={20} />
                    <div>
                      <div className="font-medium">{contact.appointment.location.value}</div>
                      <div className="text-sm text-neutral-200">{contact.appointment.location.label}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
                <h3 className="text-xl font-bold mb-6">{contact.writeUs.title}</h3>
                <div className="space-y-4 text-left">
                  <div className="flex items-center gap-3">
                    <Mail className="text-primary-200" size={20} />
                    <a
                      href={`mailto:${contact.writeUs.email}`}
                      className="hover:underline break-all"
                    >
                      {contact.writeUs.email}
                    </a>
                  </div>
                  <div className="pt-4">
                    <div className="text-sm text-neutral-200 mb-3">{contact.writeUs.socialMedia.label}</div>
                    <div className="flex gap-3">
                      <a
                        href={contact.writeUs.socialMedia.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                        aria-label="Facebook"
                      >
                        <Facebook size={24} />
                      </a>
                      <a
                        href={contact.writeUs.socialMedia.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                        aria-label="Instagram"
                      >
                        <Instagram size={24} />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-xl font-serif italic">
              "{contact.quote}"
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}
